import type {
  AgentUIMessageFullChunk,
  EditMode,
} from '../prompt/agent/tools/common/messages';
import type {GenType} from '@features/flow/flowService';

interface SelectorEdit {
  content: string;
  mode: EditMode;
}

export class StructureState {
  #rawHtml: string;

  /**
   * Contains:
   * {
   *  '#id': { content: '<div id="hello">asdasd</div>', mode: 'replace' },
   *  '.lol': { content: '.btn { color: red; }', mode: 'inner' },
   * }
   */
  #selectors = new Map<string, SelectorEdit>();

  screenId: string;
  newName: string | null = null;
  genType: GenType | null = null;

  constructor(screenId: string, html: string) {
    this.screenId = screenId;
    this.#rawHtml = html;
  }

  handleMessage(chunk: AgentUIMessageFullChunk) {
    if (!isDataChunk(chunk)) {
      return;
    }

    switch (chunk.type) {
      case 'data-clearScreen':
        this.#selectors.clear();
        this.#rawHtml = '';
        break;
      case 'data-appendHtml':
        this.#rawHtml += chunk.data.html;
        break;
      case 'data-clearOuterHtml':
        this.#selectors.set(chunk.data.selector, {
          content: '',
          mode: chunk.data.mode,
        });
        break;
      case 'data-appendOuterHtml': {
        const current = this.#selectors.get(chunk.data.selector);

        if (current === undefined) {
          console.error(
            'Unknown selector',
            chunk.data.selector,
            this.#selectors,
          );
          throw new Error(
            'Appending without clearing selector is not implemented yet.',
          );
        }

        this.#selectors.set(chunk.data.selector, {
          content: current.content + chunk.data.appendHtml,
          mode: chunk.data.mode,
        });
        break;
      }
      case 'data-setScreenName':
        this.newName = chunk.data.screenName;
        break;
      case 'data-setTheme':
        // Theme is saved to DB in the tool - no client-side handling needed.
        // Theme will be refetched during invalidation.
        break;
      case 'data-setGenType':
        this.genType = chunk.data.genType;
        break;
      case 'data-finishTurn':
        break;
      case 'data-askClarifyingQuestions':
        // Handled in streamAgent, not in StructureState
        break;
      case 'data-clarifyingQuestionsAnswered':
        // Handled in streamAgent, not in StructureState
        break;
      case 'data-pushStatusUpdate':
        // Handled in streamAgent, not in StructureState
        break;
    }
  }

  computeHtml() {
    if (this.#selectors.size === 0) {
      return this.#rawHtml;
    }

    // In the case that we're editing something
    // we always start with the raw HTML, and then apply all changes
    // Because otherwise, dealing with changes as they stream in
    // is a nightmare.
    const parser = new DOMParser();
    const doc = parser.parseFromString(this.#rawHtml, 'text/html');

    for (const [selector, edit] of this.#selectors.entries()) {
      let el = doc.querySelector(selector);

      // Handle append-style mode: append CSS to existing style tag or create new one
      if (edit.mode === 'append-style') {
        let styleEl: HTMLStyleElement | null = null;

        // Try to find existing style element
        if (el && el.tagName === 'STYLE') {
          styleEl = el as HTMLStyleElement;
        } else if (selector.startsWith('#')) {
          // If selector is an ID, look for style tag with that ID
          const existing = doc.querySelector(`style${selector}`);
          if (existing && existing.tagName === 'STYLE') {
            styleEl = existing as HTMLStyleElement;
          }
        }

        // Create new style element if not found
        if (!styleEl) {
          styleEl = doc.createElement('style');
          if (selector.startsWith('#')) {
            styleEl.id = selector.slice(1);
          }
          doc.head.appendChild(styleEl);
          console.info('Created new style element for append-style', selector);
        }

        // Append CSS content
        const existingContent = styleEl.textContent || '';
        const separator = existingContent.length > 0 ? '\n' : '';
        styleEl.textContent = existingContent + separator + edit.content;
        continue;
      }

      // If element doesn't exist and the content looks like CSS,
      // create a <style> element in the head. This handles the case where
      // the AI tries to add new style blocks for selectors that don't exist yet.
      if (!el && edit.mode === 'inner' && looksLikeCSS(edit.content)) {
        const styleEl = doc.createElement('style');
        // Extract id from selector if it's an ID selector
        if (selector.startsWith('#')) {
          styleEl.id = selector.slice(1);
        }
        doc.head.appendChild(styleEl);
        el = styleEl;
        console.info(
          'Created missing style element for selector',
          selector,
          '(content detected as CSS)',
        );
      }

      if (!el) {
        console.warn('Could not find selector', selector, '-- oops?');
        continue;
      }

      if (edit.mode === 'inner') {
        // Inner mode: update content inside the element
        // Use textContent for raw text elements (style, script, title)
        // to avoid HTML parsing of CSS/JS content
        const rawTextElements = ['STYLE', 'SCRIPT', 'TITLE'];
        if (rawTextElements.includes(el.tagName)) {
          el.textContent = edit.content;
        } else {
          el.innerHTML = edit.content;
        }
      } else {
        // Replace mode: replace the entire element (outerHTML)
        // Safety check: if content looks like raw CSS (not wrapped in <style>),
        // auto-wrap it to prevent CSS appearing as plain text in body
        if (looksLikeCSS(edit.content)) {
          console.warn(
            'Raw CSS detected in replace mode for selector',
            selector,
            '- auto-wrapping in <style> tag',
          );
          const styleEl = doc.createElement('style');
          if (selector.startsWith('#')) {
            styleEl.id = selector.slice(1);
          }
          styleEl.textContent = edit.content;
          el.outerHTML = styleEl.outerHTML;
        } else {
          el.outerHTML = edit.content;
        }
      }
    }

    return doc.documentElement.innerHTML;
  }
}

type DataChunk = AgentUIMessageFullChunk & {type: `data-${string}`};

export function isDataChunk(
  chunk: AgentUIMessageFullChunk,
): chunk is DataChunk {
  return chunk.type.startsWith('data-');
}

/**
 * Detects if content looks like CSS by checking for CSS rule patterns.
 * Looks for: selector { property: value; } patterns, @rules, CSS variables, etc.
 */
function looksLikeCSS(content: string): boolean {
  const trimmed = content.trim();

  // If it starts with < it's HTML, not raw CSS
  if (trimmed.startsWith('<')) return false;

  // Empty content is not CSS
  if (trimmed.length === 0) return false;

  // Enhanced patterns to detect CSS
  const patterns = [
    // Standard rule: .class { prop: val; } or #id { prop: val; }
    /[.#@:\w[\]="'-]+\s*\{[^}]*:[^}]*;/,
    // @rules: @media, @keyframes, @font-face, @import, etc.
    /^\s*@(media|keyframes|font-face|import|charset|supports)/m,
    // Universal selector: * { }
    /^\s*\*\s*\{/m,
    // CSS variables: --var-name:
    /--[\w-]+\s*:/,
    // Property-value pairs (common CSS properties)
    /\b(color|background|margin|padding|border|font|display|position|width|height)\s*:/,
  ];

  return patterns.some((p) => p.test(trimmed));
}
